/**
 * usePushSubscription — React hook that wires the browser to the backend's
 * Web Push API.
 *
 * Exposes:
 *   • status: 'unknown' | 'unsupported' | 'denied' | 'unsubscribed' | 'subscribed'
 *   • subscribe()    : request permission + subscribe + POST /api/push/subscribe
 *   • unsubscribe()  : cancel + DELETE /api/push/unsubscribe
 *   • testPush()     : call POST /api/push/test (devs/QA)
 *
 * Used by the bell on /settings/notifications and auto-prompts when a
 * signed-in user follows their first creator with notify_on_live_follows.
 */
import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";

function urlB64ToUint8Array(b64String) {
  const padding = "=".repeat((4 - (b64String.length % 4)) % 4);
  const base64 = (b64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

function bufferToB64(buf) {
  if (!buf) return "";
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return window.btoa(s);
}

export default function usePushSubscription({ enabled = true } = {}) {
  const [status, setStatus] = useState("unknown");
  const [busy, setBusy] = useState(false);

  const supported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  const refresh = useCallback(async () => {
    if (!supported) { setStatus("unsupported"); return; }
    if (Notification.permission === "denied") { setStatus("denied"); return; }
    try {
      const reg = await navigator.serviceWorker.getRegistration("/service-worker.js")
        || await navigator.serviceWorker.register("/service-worker.js");
      const sub = await reg.pushManager.getSubscription();
      setStatus(sub ? "subscribed" : "unsubscribed");
    } catch (_) {
      setStatus("unsupported");
    }
  }, [supported]);

  useEffect(() => { if (enabled) refresh(); }, [enabled, refresh]);

  const subscribe = useCallback(async () => {
    if (!supported) return { ok: false, reason: "unsupported" };
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setStatus(perm === "denied" ? "denied" : "unsubscribed"); return { ok: false, reason: perm }; }

      const reg = await navigator.serviceWorker.register("/service-worker.js");
      await navigator.serviceWorker.ready;

      const { data } = await api.get("/push/vapid-public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(data.public_key),
      });

      const p256dh = bufferToB64(sub.getKey("p256dh"));
      const auth = bufferToB64(sub.getKey("auth"));

      await api.post("/push/subscribe", {
        endpoint: sub.endpoint,
        keys: { p256dh, auth },
        ua: navigator.userAgent.slice(0, 200),
      });
      setStatus("subscribed");
      return { ok: true };
    } catch (e) {
      console.error("push subscribe failed", e);
      return { ok: false, reason: String(e?.message || e) };
    } finally { setBusy(false); }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return { ok: false };
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/service-worker.js");
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await api.delete(`/push/unsubscribe?endpoint=${encodeURIComponent(sub.endpoint)}`).catch(() => {});
        await sub.unsubscribe();
      }
      setStatus("unsubscribed");
      return { ok: true };
    } finally { setBusy(false); }
  }, [supported]);

  const testPush = useCallback(async () => {
    try {
      const { data } = await api.post("/push/test");
      return data;
    } catch (e) { return { sent: 0, error: String(e?.message || e) }; }
  }, []);

  return { status, supported, busy, subscribe, unsubscribe, testPush, refresh };
}
